'use client'

import { LazyMotion, domAnimation, m, AnimatePresence } from 'framer-motion'
import { tw } from 'utils/classnames'
import { Button } from '@repo/design-system-cf/components/button'
import { usePricesForUser } from '@repo/cf-user/hooks/use-prices'
import { Typography } from '@repo/design-system-cf/components/typography'
import { ActivateTrial } from 'payments/activate-trial-button'
import { UPGRADE_SOURCES } from 'payments/upgrade-sources'

export function YearlyBanner({
  title,
  subTitle,
  ctaDesktop,
  ctaMobile,
  cancelText,
}: {
  title: string
  subTitle: string
  ctaDesktop: string
  ctaMobile: string
  cancelText: string
}) {
  const { prices, currency } = usePricesForUser()

  const ctaTextDesktop = ctaDesktop
    .replace('{currency}', currency.symbol)
    .replace('{price}', String(prices.yearlyPromo))

  return (
    <LazyMotion features={domAnimation}>
      <m.div
        layout
        initial={false}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={tw(
          'fixed-banner relative overflow-hidden',
          'text-service-white flex w-full flex-col items-center justify-center gap-[10px] p-[10px] px-5 shadow-md md:flex-row bg-cf-blue-100',
        )}
      >
        <div
          className={tw(
            'flex w-full flex-col items-center justify-between md:flex-row',
            'max-w-[1440px]',
          )}
        >
          <AnimatePresence mode="wait">
            <m.div
              key="collapsed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className={tw('flex w-full flex-col items-center justify-between md:flex-row')}
            >
              <p className={tw('text-16 ml-[10px]')}>
                <span
                  className={tw(
                    'text-cf-grey-900 block font-bold leading-[22px]',
                    'text-16 md:text-24',
                  )}
                >
                  {title}
                </span>
                {subTitle ? (
                  <span
                    className={tw('text-14 text-cf-grey-900 mb-2 block md:mb-0 md:font-semibold')}
                  >
                    {subTitle}
                  </span>
                ) : null}
              </p>
              <ActivateTrial.Root upgradeSource={UPGRADE_SOURCES.BOTTOM_VIEWPORT_BANNER}>
                <div className="flex flex-col items-center gap-1">
                  <Button
                    asChild
                    variant="primary"
                    size="large"
                    className="hidden md:inline-flex uppercase bg-secondary-500 hover:bg-secondary-700"
                  >
                    <ActivateTrial.Trigger>
                      <ActivateTrial.Label>{ctaTextDesktop}</ActivateTrial.Label>
                    </ActivateTrial.Trigger>
                  </Button>
                  <Button
                    asChild
                    variant="primary"
                    className="inline-flex md:hidden uppercase bg-secondary-500 hover:bg-secondary-700"
                  >
                    <ActivateTrial.Trigger>
                      <ActivateTrial.Label>{ctaMobile}</ActivateTrial.Label>
                    </ActivateTrial.Trigger>
                  </Button>
                  <Typography variant="cf-label-02" className="text-cf-black">
                    {cancelText}
                  </Typography>
                </div>
                <ActivateTrial.Dialogs />
              </ActivateTrial.Root>
            </m.div>
          </AnimatePresence>
        </div>
      </m.div>
    </LazyMotion>
  )
}
